import { Box, Dialog, DialogTitle, Step, StepContent, StepLabel, Stepper } from "@mui/material";
import { useCallback, useState } from "react";
import { usePropertyId } from "../../../../../common/hooks/usePropertyId";
import DateAndOccupancyEditor from "./DateAndOccupancyEditor";
import RoomSelector from "./RoomSelector";
import CustomerEditor from "./CustomerEditor";
import PaymentEditor from "./PaymentEditor";
import Comments from "./Comments";
import SnackbarError from "../../../../../common/SnackbarError";
import { useFormStatus } from "../../../../../common/inputs/useFormStatus";
import { withStepperButtons } from "../../../../../common/withStepperButtons";
import { withQueryData } from "../../../../../common/withQueryData";
import { useReservationsApi } from "../../../../../common/hooks/useReservationsApi";
import { useConfigurationData } from "../../../../../common/hooks/useConfigurationData";

const style = ({ theme }) => ({
    '& .MuiPaper-root': {
        width: '80%',
        maxWidth: '800px',
    }
});

const emptyReservation = {
    adults: 1,
    checkin: null,
    checkout: null,
    comments: '',
    customer: {
        address: '',
        countryCode: null,
        email: '',
        firstName: '',
        lastName: '',
        loyaltyNumber: null,
        countryPrefix: null,
        phoneNumber: '',
    },
    planId: 0,
    roomId: 0
}

const steps = [
    {
        label: 'Select dates and occupancy',
        component: withStepperButtons(DateAndOccupancyEditor),
        nextButton: true,
    },
    {
        label: 'Select room and plan',
        component: withStepperButtons(RoomSelector),
        nextButton: false,
    },
    {
        label: 'Enter customer information',
        component: withStepperButtons(CustomerEditor),
        nextButton: true,
    },
    {
        label: 'Enter payment details',
        component: withStepperButtons(PaymentEditor),
        nextButton: true,
    },
    {
        label: 'Comments',
        component: withStepperButtons(Comments),
        nextButton: true,
    },
];

const ReservationCreator = ({ onClose, onSuccess }) => {
    const [error, setError] = useState(null);

    const { countriesQueryResults } = useConfigurationData();

    return <>
        <Dialog sx={style} open>
            <DialogTitle>Create a new reservation</DialogTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100px' }}>
                <DialogContentWithQueryData
                    queryData={countriesQueryResults}
                    onClose={onClose}
                    onSuccess={onSuccess}
                    onError={(error) => setError(error)}
                />
            </Box>
        </Dialog>
        <SnackbarError error={error} onClose={() => setError(null)} />
    </>;
}

const DialogContent = ({ data: countries, onClose, onSuccess, onError }) => {
    const [activeStep, setActiveStep] = useState(0);

    const { createReservation } = useReservationsApi();

    const localCountryCode = new Intl.DateTimeFormat().resolvedOptions().locale.split('-').pop();
    const defaultCountry = countries.find(country => country.code.toLowerCase() === localCountryCode);

    const [reservation, setReservation] = useState({
        ...emptyReservation,
        customer: {
            ...emptyReservation.customer,
            countryCode: defaultCountry.code,
            countryPrefix: defaultCountry.phonePrefix
        }
    });

    const [status, setStatus, statusCodes] = useFormStatus();

    const propertyId = usePropertyId();

    const handleSubmit = async () => {
        setStatus(statusCodes.SAVING);

        try {
            const order = await createReservation(propertyId, reservation);

            onSuccess(order);
        }
        catch (error) {
            _onError(error);
        }
    }

    const _onError = (error) => {
        console.error(error);
        setStatus(statusCodes.AWAITING_USER_INPUT);
        onError(error);
    }

    const handleNext = () => {
        if (activeStep >= steps.length - 1) {
            handleSubmit();
        }
        else setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = (isFirst) => {
        if (isFirst) {
            onClose();
        }
        else setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleInputChange = useCallback((fields) => {
        let newReservation = { ...reservation };

        let changed = false;

        fields.forEach(({ id, value }) => {
            const pathToValue = id.split('.');

            let objectToChange = newReservation;

            while (pathToValue.length > 1) {
                objectToChange = objectToChange[pathToValue.shift()];
            }

            if (objectToChange[pathToValue[0]] !== value) {
                changed = true;
                objectToChange[pathToValue[0]] = value;
            }
        });

        changed && setReservation(newReservation);
        return changed;
    }, [reservation, setReservation])

    return <Stepper activeStep={activeStep} orientation="vertical" sx={{ padding: '0px 20px 20px 20px' }}>
        {steps.map((step, index) => {
            const Content = step.component;

            return <Step key={step.label}>
                <StepLabel>
                    {step.label}
                </StepLabel>
                <StepContent>
                    <Content
                        first={index === 0}
                        last={index === steps.length - 1}
                        data={reservation}
                        propertyId={propertyId}
                        handleInputChange={handleInputChange}
                        handleNext={handleNext}
                        handleBack={handleBack}
                        loading={status === statusCodes.SAVING}
                        disableNextButton={!step.nextButton}
                    />
                </StepContent>
            </Step>
        })}
    </Stepper>
}

const DialogContentWithQueryData = withQueryData(DialogContent)

export default ReservationCreator;